'use client'

import { cn } from '@brew/ui/cn'
import { Input } from '@brew/ui/input'
import { useMemo, useState } from 'react'
import {
  ROUNDED_SEARCH_INPUT_CLASSNAME,
  RoundedSearchBar,
} from '@/components/audience/rounded-search-bar'
import { MARKETING_CANVAS_BG } from '@/components/marketing/v2/marketing-layout'
import {
  type IntegrationMeta,
  integrationData,
} from '@/lib/integrations/catalog'
import { TYPE } from '@/lib/ui/typography'
import { useActiveIntegrations } from './hooks/use-active-integrations'
import { IntegrationCard } from './integration-card'

export type IntegrationListMode = 'app' | 'public'

interface IntegrationListProps {
  mode?: IntegrationListMode
  initialActiveProviders?: Array<string>
}

interface IntegrationSection {
  id: 'data_out' | 'data_in'
  title: string
  subtitle: string
  items: Array<IntegrationMeta>
}

const SECTION_COPY: Record<
  IntegrationSection['id'],
  { title: string; subtitle: string }
> = {
  data_out: {
    title: 'Email destinations',
    subtitle: 'Send the emails you generate to your existing ESP.',
  },
  data_in: {
    title: 'Data sources',
    subtitle: 'Pull design files, customer events, and feedback into Brew.',
  },
}

export default function IntegrationList({
  mode = 'app',
  initialActiveProviders = [],
}: IntegrationListProps) {
  const [search, setSearch] = useState('')
  const { activeIntegrations, count: enabledCount } = useActiveIntegrations()

  const activeProviderIds = useMemo(() => {
    const providers =
      activeIntegrations.length > 0 || enabledCount > 0
        ? activeIntegrations.map((i) => i.provider)
        : initialActiveProviders
    return new Set(providers)
  }, [activeIntegrations, enabledCount, initialActiveProviders])

  const resolvedEnabledCount =
    enabledCount > 0 || activeIntegrations.length > 0
      ? enabledCount
      : initialActiveProviders.length

  const totalCount = integrationData.length

  const filteredIntegrations = useMemo(() => {
    const query = search.toLowerCase().trim()
    if (!query) {
      return integrationData
    }
    return integrationData.filter(
      (integration) =>
        integration.name.toLowerCase().includes(query) ||
        integration.shortDescription.toLowerCase().includes(query)
    )
  }, [search])

  const sections = useMemo<Array<IntegrationSection>>(() => {
    const out = filteredIntegrations.filter(
      (i) => i.dataDirection === 'data_out'
    )
    const inn = filteredIntegrations.filter(
      (i) => i.dataDirection === 'data_in'
    )
    const built: Array<IntegrationSection> = [
      { id: 'data_out', items: out, ...SECTION_COPY.data_out },
      { id: 'data_in', items: inn, ...SECTION_COPY.data_in },
    ]
    return built.filter((section) => section.items.length > 0)
  }, [filteredIntegrations])

  const isPublic = mode === 'public'

  return (
    <div
      className={cn('w-full', {
        'mx-auto mt-2 max-w-[1320px] px-4': !isPublic,
      })}
    >
      {!isPublic ? (
        <header className="mt-4 mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p className={cn(TYPE.bodyMuted, 'max-w-xl leading-relaxed')}>
            Connect your stack to Brew. Pull design files and event data in,
            then push the emails you generate out to your existing ESP.
          </p>
          <span
            className={cn(
              TYPE.supportingMedium,
              'flex w-fit shrink-0 items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-0.5 text-foreground'
            )}
          >
            <span className="size-1.5 rounded-full bg-emerald-500" />
            {resolvedEnabledCount} of {totalCount} connected
          </span>
        </header>
      ) : null}

      <RoundedSearchBar
        className={cn('mb-7 h-11', { [MARKETING_CANVAS_BG]: isPublic })}
      >
        <Input
          type="search"
          name="integration-search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="Search integrations..."
          aria-label="Search integrations"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          className={cn(ROUNDED_SEARCH_INPUT_CLASSNAME, 'h-full text-sm', {
            'bg-transparent': isPublic,
          })}
        />
      </RoundedSearchBar>

      {sections.length === 0 ? (
        <div className={cn(TYPE.bodyMuted, 'py-16 text-center')}>
          No integrations match &ldquo;{search}&rdquo;.
        </div>
      ) : (
        <div className="flex flex-col gap-10 pb-12">
          {sections.map((section) => (
            <section key={section.id} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1 border-border border-b pb-2 sm:flex-row sm:items-baseline sm:gap-3">
                <h2 className={TYPE.section}>{section.title}</h2>
                <p className={TYPE.supporting}>{section.subtitle}</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {section.items.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    isConnected={activeProviderIds.has(integration.id)}
                    mode={mode}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
